import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { PureComponent, ReactNode } from 'react';
import { Address } from './AddAddressModal';
import { AddressFormInput, CloseBtIcon } from './BookLabTestsUtils';
import fetchPatientAddress, { Add } from '../../../../api/fetchPatientAddress';
import { Input, notification, Radio, RadioChangeEvent, Space } from 'antd';
import fetchLatlngFromPincode from '../../../../api/fetchLatlngFromPincode';
import { track } from '../../../../tracking';
import { getMySingleDoctorId } from '../../../../store/idbHandler';

function disableInputs() {
    const ids = ['city', 'state', 'pincode'];
    ids.forEach((id) => {
        const input = document.getElementById(id) as HTMLInputElement;
        if (input) {
            if (input.value) input.disabled = true;
            else input.disabled = false;
        }
    });
}

const emptyFormInput = {
    line: '',
    landmark: '',
    city: '',
    state: '',
    city_id: '',
    place_id: '',
    coordinates: '',
    pincode: '',
};

type State = {
    formInput: AddressFormInput;
    patientAddressArray: Add[];
    isFetchingAddress?: boolean;
    selectedOption: number;
    isError?: boolean;
};

type Props = {
    closeMe: () => void;
    setAddress: (add: Address) => void;
    addFormInput?: AddressFormInput;
    currentOptionId?: string;
    patientId: string;
    isEditingAddress?: boolean;
    showOptions?: boolean;
    pincode: string;
};

class AddAddressModalCC extends PureComponent<Props, State> {
    state: State = {
        formInput:
            this.props.isEditingAddress && this.props.addFormInput
                ? this.props.addFormInput
                : emptyFormInput,
        patientAddressArray: [],
        selectedOption: 0,
    };

    start = new Date().getTime();

    componentDidMount = (): void => {
        const { patientId, showOptions, currentOptionId } = this.props;
        const docId = getMySingleDoctorId() || '';

        if (showOptions) {
            this.setState({
                isFetchingAddress: true,
            });
            fetchPatientAddress(docId, patientId)
                .then((i) => {
                    const addressArray = i.address.filter((i) => i.address.coordinates?.trim());
                    this.setState({
                        patientAddressArray: addressArray,
                        selectedOption: addressArray.findIndex((i) => i._id === currentOptionId),
                    });
                })
                .catch((e) => {
                    console.log(e);
                })
                .finally(() => {
                    this.setState({
                        isFetchingAddress: false,
                    });
                });
        } else this.init();
        disableInputs();
    };

    componentDidUpdate = (prevProps: Props, prevState: State): void => {
        if (prevState.patientAddressArray !== this.state.patientAddressArray) this.init();
    };

    init = (): void => {
        const { pincode } = this.state.formInput;
        const formInputs = document.querySelectorAll('.add-address-form input');
        Array.from(formInputs).forEach((input) => {
            input.addEventListener('blur', () => {
                input.classList.add('border-darwin-status-warning-500');
                if (input.id === 'landmark') {
                    setTimeout(() => {
                        if (!this.state.formInput.coordinates) {
                            notification.error({
                                message: 'Select address from suggestions',
                                duration: 5,
                            });
                            this.setState({
                                isError: true,
                                formInput: {
                                    ...this.state.formInput,
                                    landmark: '',
                                },
                            });
                        }
                    }, 500);
                }
            });
        });

        const initAutoSuggest = async () => {
            const { pincode: initialPincode } = this.props;
            const result = await fetchLatlngFromPincode(pincode || initialPincode);
            const latLng = result.results?.[0]?.geometry?.location;
            const input = document.getElementById('landmark') as HTMLInputElement;

            if (!input) {
                return;
            }

            let defaultBounds;
            if (latLng)
                defaultBounds = {
                    north: latLng.lat + 0.2,
                    south: latLng.lat - 0.2,
                    east: latLng.lng + 0.2,
                    west: latLng.lng - 0.2,
                };

            const options = {
                bounds: defaultBounds,
                strictBounds: false,
                componentRestrictions: { country: 'in' },
                fields: [
                    'name',
                    'geometry.location',
                    'place_id',
                    'formatted_address',
                    'address_components',
                ],
            };
            try {
                const autocomplete = new window.google.maps.places.Autocomplete(input, options);
                window.google.maps.event.addListener(autocomplete, 'place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (!place || !place.geometry || !place.geometry.location) {
                        return;
                    }
                    const lat = place.geometry?.location.lat();
                    const lng = place.geometry.location.lng();
                    const { name = '', place_id = '', address_components = [] } = place;
                    const formatted_address: string[] = [name];
                    const additionalInfo: {
                        pincode?: string;
                        city?: string;
                        state?: string;
                    } = {};
                    address_components?.forEach((addComp) => {
                        if (addComp.types.includes('postal_code')) {
                            additionalInfo.pincode = addComp.long_name;
                        }
                        if (addComp.types.includes('locality')) {
                            additionalInfo.city = addComp.long_name;
                        }
                        if (addComp.types.includes('administrative_area_level_1')) {
                            additionalInfo.state = addComp.long_name;
                        }
                        if (
                            addComp.types.includes('sublocality') ||
                            addComp.types.includes('route')
                        ) {
                            formatted_address?.push(addComp.long_name);
                        }
                    });

                    const { formInput } = this.state;
                    const { city = '', state = '', pincode = '' } = additionalInfo;
                    this.setState({
                        formInput: {
                            ...formInput,
                            landmark: formatted_address?.filter(Boolean).join(', ') || '',
                            city,
                            state,
                            place_id,
                            pincode,
                            coordinates: `${lat},${lng}`,
                        },
                        isError: false,
                    });
                    disableInputs();
                    const ele = document.getElementById('line');
                    ele?.focus();
                });
            } catch (err) {
                console.log(err);
            }
        };

        if (window.google) {
            initAutoSuggest();
        }
    };

    formInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { formInput } = this.state;
        const { id, value } = e.target;
        this.setState({
            formInput: {
                ...formInput,
                [id]: value,
            },
        });
    };

    saveAddressHandler = (): void => {
        const { setAddress } = this.props;
        const { patientAddressArray, selectedOption, formInput } = this.state;

        if (patientAddressArray.length !== 0) {
            const { address, _id } = patientAddressArray[selectedOption];
            setAddress({
                _id: _id,
                address: address.line2,
                line: address.line1,
                latLng: address.coordinates,
                pincode: address.pincode,
                city: address.city,
                state: address.state,
                placeID: address.place_id,
            });
        } else {
            setAddress({
                _id: formInput._id,
                address: formInput.landmark,
                line: formInput.line,
                latLng: formInput.coordinates,
                pincode: formInput.pincode,
                city: formInput.city,
                state: formInput.state,
                placeID: formInput.place_id,
            });
        }

        track('ADD_ADDRESS_LABS', {
            timeToAddAddress: `${(new Date().getTime() - this.start) / 1000} seconds`,
        });
    };

    isValidAddress = (): boolean => {
        const { formInput } = this.state;
        const { line, landmark, city, state, pincode } = formInput;

        return (
            line.trim().length >= 1 &&
            landmark.trim().length >= 3 &&
            city.trim().length >= 3 &&
            state.trim().length >= 3 &&
            pincode.trim().length === 6
        );
    };

    editAddressHandler = (index: number): void => {
        const { patientAddressArray } = this.state;
        if (patientAddressArray[index]) {
            const { address, _id } = patientAddressArray[index];
            const { line1, line2, city, state, city_id, place_id, pincode, coordinates } = address;
            this.setState({
                formInput: {
                    _id,
                    line: line1,
                    landmark: line2,
                    city,
                    state,
                    city_id,
                    place_id,
                    pincode,
                    coordinates,
                },
                patientAddressArray: [],
            });
        }
    };

    render(): ReactNode {
        const { closeMe } = this.props;
        const {
            isFetchingAddress,
            patientAddressArray,
            selectedOption,
            formInput,
            isError,
        } = this.state;
        const {
            formInputChangeHandler,
            saveAddressHandler,
            isValidAddress,
            editAddressHandler,
        } = this;
        const { line, landmark, city, state, pincode } = formInput;

        return (
            <Hotkeys keyName="esc">
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 20 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="font-lato select-none"
                >
                    <div className="modal-overlay" onClick={closeMe}></div>
                    <div className="modal-content w-592 max-h-504 h-4/5 font-lato font-400 rounded-8">
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between px-16 py-14 bg-neutral-9 rounded-t-8">
                                <p className="font-700 text-16">Add New Address</p>
                                <CloseBtIcon closeMe={closeMe} />
                            </div>

                            {isFetchingAddress ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-24 h-24 fill-darwin-primary-500 animate-spin"
                                    >
                                        <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                                    </svg>
                                </div>
                            ) : patientAddressArray.length > 0 ? (
                                <div className="flex-1 text-14 space-y-8 pt-4 px-16 pb-14 overflow-auto">
                                    <Radio.Group
                                        onChange={(e: RadioChangeEvent) => {
                                            this.setState({
                                                selectedOption: e.target.value,
                                            });
                                        }}
                                        value={selectedOption}
                                        className="w-full"
                                    >
                                        <Space direction="vertical" className="w-full">
                                            {patientAddressArray?.map((i, index) => (
                                                <Radio
                                                    value={index}
                                                    className="relative border-b border-darwin-neutral-100 px-8 pt-0 pb-10 w-full space-y-10"
                                                >
                                                    <p className="font-700">{i.address.line1}</p>
                                                    <p>
                                                        {[
                                                            i.address.line2,
                                                            i.address.city,
                                                            i.address.state,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                    </p>
                                                    <p>Pincode: {i.address.pincode}</p>
                                                    <svg
                                                        className="absolute right-12 top-16 w-14 h-13 fill-darwin-neutral-900"
                                                        viewBox="0 0 14 13"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        onClick={() => editAddressHandler(index)}
                                                    >
                                                        <path d="M1.84375 8.96094C1.9375 8.63281 2.125 8.32812 2.35938 8.09375L9.48438 0.96875C10.0703 0.382812 11.0312 0.382812 11.6172 0.96875L12.5312 1.88281C12.6016 1.95312 12.6719 2.04688 12.7188 2.11719C13.1172 2.70312 13.0469 3.5 12.5312 4.01562L5.40625 11.1406C5.38281 11.1641 5.33594 11.1875 5.3125 11.2344C5.07812 11.4219 4.82031 11.5625 4.53906 11.6562L1.70312 12.4766C1.51562 12.5469 1.30469 12.5 1.16406 12.3359C1 12.1953 0.953125 11.9844 1 11.7969L1.84375 8.96094ZM2.38281 11.1172L4.21094 10.5781C4.25781 10.5547 4.32812 10.5312 4.375 10.5078L3.53125 10.3438C3.34375 10.2969 3.17969 10.1562 3.15625 9.96875L2.99219 9.125C2.96875 9.17188 2.94531 9.24219 2.92188 9.28906L2.38281 11.1172ZM5.21875 9.73438L9.97656 4.97656L8.5 3.52344L3.76562 8.28125L4 9.47656L5.21875 9.73438Z" />
                                                    </svg>
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Radio.Group>
                                    <div
                                        className="flex items-center space-x-8 cursor-pointer w-max pt-4"
                                        onClick={() => {
                                            this.setState({ patientAddressArray: [] });
                                        }}
                                    >
                                        <svg
                                            className="w-12 h-12 fill-darwin-primary-500"
                                            viewBox="0 0 11 11"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M6.25 1.375C6.25 0.976562 5.89844 0.625 5.5 0.625C5.07812 0.625 4.75 0.976562 4.75 1.375V4.75H1.375C0.953125 4.75 0.625 5.10156 0.625 5.5C0.625 5.92188 0.953125 6.25 1.375 6.25H4.75V9.625C4.75 10.0469 5.07812 10.375 5.5 10.375C5.89844 10.375 6.25 10.0469 6.25 9.625V6.25H9.625C10.0234 6.25 10.375 5.92188 10.375 5.5C10.375 5.10156 10.0234 4.75 9.625 4.75H6.25V1.375Z" />
                                        </svg>
                                        <p className="font-700 text-darwin-primary-500">
                                            Add new address
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 text-14 px-16 py-14 overflow-auto space-y-16 add-address-form">
                                    <div className="space-y-6">
                                        <p className="font-700">Address</p>

                                        <div className="relative">
                                            <Input
                                                id="landmark"
                                                className="p-10 pl-38 rounded-8"
                                                placeholder="Area, street name..."
                                                autoFocus
                                                value={landmark}
                                                onChange={formInputChangeHandler}
                                                required
                                            />
                                            <svg
                                                className="absolute top-15 left-13 w-16 h-16 fill-darwin-neutral-1000"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z" />
                                            </svg>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <p className="font-700">House / Flat Name and No.</p>
                                        <Input
                                            id="line"
                                            className="p-10 rounded-8"
                                            placeholder="Ex- 2053, Anagha Enclave, 4th Floor, D2 Flat"
                                            value={line}
                                            onChange={formInputChangeHandler}
                                            required
                                        />
                                    </div>

                                    <div className="space-x-16 grid grid-cols-2">
                                        <div className="space-y-6">
                                            <p className="font-700">City</p>
                                            <Input
                                                id="city"
                                                className="p-10 rounded-8"
                                                placeholder="Ex- Bengalore"
                                                value={city}
                                                onChange={formInputChangeHandler}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-6">
                                            <p className="font-700">State</p>
                                            <Input
                                                id="state"
                                                className="p-10 rounded-8"
                                                placeholder="Ex- Karnataka"
                                                value={state}
                                                onChange={formInputChangeHandler}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <p className="font-700">Pincode</p>
                                        <Input
                                            id="pincode"
                                            className="p-10 rounded-8"
                                            placeholder="560102"
                                            value={pincode}
                                            onChange={formInputChangeHandler}
                                            type="number"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between px-16 py-9 bg-neutral-9 rounded-b-8">
                                <p
                                    className="text-neutral-3 font-700 cursor-pointer"
                                    onClick={closeMe}
                                >
                                    Cancel
                                </p>
                                <button
                                    className="btn--small primary-btn px-16 py-8"
                                    onClick={saveAddressHandler}
                                    disabled={
                                        patientAddressArray.length
                                            ? selectedOption === -1
                                            : !isValidAddress() || isError
                                    }
                                >
                                    Save Address
                                </button>
                            </div>
                        </div>
                    </div>

                    <style>
                        {`
                                .pac-item {
                                    padding: 12px 16px !important;
                                    font-size: 14px !important;
                                }

                                .hdpi.pac-logo:after {
                                    display: none !important;
                                }

                                .pac-item-query {
                                    font-size: 16px !important;
                                }

                                .add-address-form input:valid {
                                    border-color: black;
                                }

                               `}
                    </style>
                </motion.div>
            </Hotkeys>
        );
    }
}

export default AddAddressModalCC;
